import { useMemo } from "react";
import {
  Button,
  Flex,
  Icon,
  Icons,
  Paragraph,
  type TableColumnType,
} from "@ir/ui";
import {
  ConfigDeductionSlaConfig,
  type IConfigDeductionSlaConfig,
} from "@src/domain/entities/config";
import { Permissions } from "@src/domain/permissions";
import HasAnyPermission from "@src/presentation/components/permissions/HasAnyPermission";
import {
  CONFIG_COLUMN_VERTICAL_ALIGN,
  ConfigListTable,
} from "./configTable.shared";

export interface ConfigDeductionSlaConfigRow extends IConfigDeductionSlaConfig {
  key: string;
}

function mapToRows(
  configs: ConfigDeductionSlaConfig[],
): ConfigDeductionSlaConfigRow[] {
  return configs.map((entity) => ({
    ...ConfigDeductionSlaConfig.toJSON(entity),
    key: String(entity.id),
  }));
}

function buildColumns(
  onEdit: (config: ConfigDeductionSlaConfig) => void,
  byId: Map<number, ConfigDeductionSlaConfig>,
): TableColumnType<ConfigDeductionSlaConfigRow>[] {
  return [
    {
      title: "Config Key",
      dataIndex: "configKey",
      key: "configKey",
      align: "left",
      verticalAlign: CONFIG_COLUMN_VERTICAL_ALIGN,
      width: 280,
      fixed: "left",
    },
    {
      title: "Config Value",
      dataIndex: "configValue",
      key: "configValue",
      align: "left",
      verticalAlign: CONFIG_COLUMN_VERTICAL_ALIGN,
      width: 140,
      cellRender: (value: unknown) => (
        <Paragraph className="m-0">
          {value == null || value === "" ? "—" : String(value)}
        </Paragraph>
      ),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      align: "left",
      verticalAlign: CONFIG_COLUMN_VERTICAL_ALIGN,
      width: 420,
      cellRender: (value: unknown) => (
        <Paragraph className="m-0">
          {typeof value === "string" && value.trim() ? value : "—"}
        </Paragraph>
      ),
    },
    {
      title: "Action",
      dataIndex: "id",
      key: "action",
      align: "left",
      verticalAlign: CONFIG_COLUMN_VERTICAL_ALIGN,
      width: 80,
      fixed: "right",
      cellRender: (_value: unknown, record: ConfigDeductionSlaConfigRow) => {
        const config = byId.get(record.id);
        if (!config) return null;
        return (
          <Flex align="center" justifyContent="start" gap={0.25}>
            <HasAnyPermission requiredPermissions={[Permissions.CONFIG_EDIT]}>
              <Button
                size="small"
                variant="clear"
                color="grey"
                aria-label={`Edit SLA config ${config.configKey}`}
                onPress={() => onEdit(config)}
              >
                <Icon name={Icons.Edit} size="small" />
              </Button>
            </HasAnyPermission>
          </Flex>
        );
      },
    },
  ];
}

interface ConfigDeductionSlaConfigsTableProps {
  configs: ConfigDeductionSlaConfig[];
  search: string;
  setSearch: (search: string) => void;
  setPage: (page: number) => void;
  onEdit: (config: ConfigDeductionSlaConfig) => void;
}

export const ConfigDeductionSlaConfigsTable = ({
  configs,
  search,
  setSearch,
  setPage,
  onEdit,
}: ConfigDeductionSlaConfigsTableProps) => {
  const byId = useMemo(
    () => new Map(configs.map((item) => [item.id, item])),
    [configs],
  );
  const columns = useMemo(() => buildColumns(onEdit, byId), [byId, onEdit]);
  const dataSource = useMemo(() => mapToRows(configs), [configs]);

  return (
    <ConfigListTable
      columns={columns}
      dataSource={dataSource}
      search={search}
      entityLabel="SLA configs"
      ariaLabel="SLA configs table"
      setSearch={setSearch}
      setPage={setPage}
    />
  );
};
